/**
 * FilterLocationMap — Google Maps + Places Autocomplete for the "Where" filter.
 * - Roadmap view (satellite tiles are a separate billing tier)
 * - Greenish-blue marker
 * - Bounded to Bengaluru + 20km
 * - NO external Google Maps link anywhere
 * - Auto-detect (browser geolocation) button with REST-API reverse-geocoding fallback
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, LocateFixed, X } from 'lucide-react';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
// Bengaluru center
const BLURU_CENTER = { lat: 12.9716, lng: 77.5946 };
// 20km bound box approx
const BLURU_BOUNDS = {
  north: 13.15, south: 12.79, east: 77.77, west: 77.41,
};

function isPreviewEnv() {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
}

/**
 * Load the Maps JS API exactly once, regardless of how many components call this.
 * Uses s.onload so it works whether or not a `callback=` param was used.
 */
function loadMapsScript(onReady) {
  if (window.google?.maps?.Map) { onReady(); return; }
  const existing = document.getElementById('gmap-script');
  if (existing) {
    // Script tag exists — either still loading or already loaded
    if (window.google?.maps?.Map) { onReady(); return; }
    existing.addEventListener('load', () => onReady());
    return;
  }
  const s = document.createElement('script');
  s.id    = 'gmap-script';
  s.src   = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places&v=weekly`;
  s.async = true;
  s.onload = () => onReady();
  document.head.appendChild(s);
}

// Greenish-blue custom SVG marker
function makeMarkerIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
    <path d="M16 0C7.164 0 0 7.164 0 16c0 9.941 14 24 16 24s16-14.059 16-24C32 7.164 24.836 0 16 0z" fill="#0d9488"/>
    <circle cx="16" cy="16" r="6" fill="white"/>
  </svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(32, 40),
    anchor: new window.google.maps.Point(16, 40),
  };
}

export default function FilterLocationMap({ value, onChange, onAutoDetect, userId, sessionId, distanceKm = 10, onDistanceChange, showOnline = true, onShowOnlineChange }) {
  const inputRef        = useRef(null);
  const mapRef          = useRef(null);
  const mapInstanceRef  = useRef(null);
  const markerRef       = useRef(null);
  // ready = Maps JS API is fully loaded and initialised
  const [ready, setReady]           = useState(!!window.google?.maps?.Map);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError]     = useState('');
  const [mapLoaded, setMapLoaded]   = useState(false);
  const [mapError, setMapError]     = useState(false);
  const [inputText, setInputText]   = useState(value?.name || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const skipMap = isPreviewEnv();

  // If Maps was already preloaded by ActivitiesPage, mark ready immediately
  useEffect(() => {
    if (window.google?.maps?.Map) { setReady(true); return; }
    if (!API_KEY) return;
    loadMapsScript(() => setReady(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep input text in sync with the `value` prop (e.g. when cleared externally)
  useEffect(() => {
    setInputText(value?.name || '');
  }, [value]);

  // Init map once Maps JS API is ready
  useEffect(() => {
    if (skipMap || !ready || !mapRef.current || mapInstanceRef.current) return;
    let tilesTimer = null;

    // Capture global auth-failure hook BEFORE creating map instance
    // This fires immediately if the key is invalid or Places isn't authorised
    const prevAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      setMapError(true);
      if (typeof prevAuthFailure === 'function') prevAuthFailure();
    };

    try {
      const map = new window.google.maps.Map(mapRef.current, {
        center: value ? { lat: value.lat, lng: value.lng } : BLURU_CENTER,
        zoom: value ? 14 : 11,
        mapTypeId: 'roadmap',
        restriction: { latLngBounds: BLURU_BOUNDS, strictBounds: false },
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        clickableIcons: false,
        gestureHandling: 'cooperative',
      });
      mapInstanceRef.current = map;

      // If no tilesloaded within 5s treat as auth error
      tilesTimer = setTimeout(() => setMapError(true), 5000);
      map.addListener('tilesloaded', () => {
        clearTimeout(tilesTimer);
        // Only show map if no auth failure was detected
        setMapLoaded((prev) => { return prev ? prev : true; });
      });

      if (value) placeMarker({ lat: value.lat, lng: value.lng });
    } catch (err) {
      console.error('FilterLocationMap init:', err);
      setMapError(true);
    }
    return () => {
      if (tilesTimer) clearTimeout(tilesTimer);
      // Restore previous auth failure handler
      window.gm_authFailure = prevAuthFailure;
    };
  }, [ready, skipMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch suggestions using the new AutocompleteSuggestion API (no .pac-container widget)
  const fetchSuggestions = useCallback(async (input) => {
    if (!input || input.length < 2 || !window.google?.maps?.places?.AutocompleteSuggestion) {
      setSuggestions([]);
      return;
    }
    try {
      const { suggestions: res } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        locationRestriction: BLURU_BOUNDS,
      });
      setSuggestions(res.map((s) => ({ text: s.placePrediction.text.toString(), prediction: s.placePrediction })));
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    }
  }, []);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputText(v);
    if (v) fetchSuggestions(v);
    else { setSuggestions([]); setShowSuggestions(false); }
  };

  const selectSuggestion = async (item) => {
    setInputText(item.text);
    setSuggestions([]);
    setShowSuggestions(false);
    try {
      const place = item.prediction.toPlace();
      await place.fetchFields({ fields: ['location'] });
      const loc = place.location;
      if (loc) {
        handleLocationSelected({ lat: loc.lat(), lng: loc.lng(), name: item.text, isAutoDetect: false });
      }
    } catch { /* silent */ }
  };

  const placeMarker = useCallback((coords) => {
    const map = mapInstanceRef.current;
    if (!map || !window.google?.maps?.Marker) return;
    if (markerRef.current) markerRef.current.setMap(null);
    markerRef.current = new window.google.maps.Marker({
      position: coords,
      map,
      icon: makeMarkerIcon(),
      title: '',
      clickable: false,
    });
    map.panTo(coords);
    map.setZoom(14);
  }, []);

  const handleLocationSelected = useCallback(({ lat, lng, name, isAutoDetect }) => {
    placeMarker({ lat, lng });
    onChange({ lat, lng, name, isAutoDetect });

    // Save to analytics (fire-and-forget)
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/analytics/location-search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ location_name: name, lat, lng, is_auto_detect: isAutoDetect, user_id: userId || null, session_id: sessionId || null }),
    }).catch(() => {});
  }, [placeMarker, onChange, userId, sessionId]);

  const handleAutoDetect = () => {
    if (!navigator.geolocation) { setGeoError('Geolocation not supported'); return; }
    setGeoLoading(true);
    setGeoError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        let name = null;

        // Try Google Maps JS Geocoder first (available if map loaded)
        if (window.google?.maps?.Geocoder) {
          try {
            const geocoder = new window.google.maps.Geocoder();
            const res = await new Promise((resolve) => geocoder.geocode({ location: { lat, lng } }, resolve));
            if (res?.[0]?.formatted_address) name = res[0].formatted_address;
          } catch (_) {}
        }

        // Fallback: Google Geocoding REST API (works even if Maps JS not loaded)
        if (!name && API_KEY) {
          try {
            const r = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${API_KEY}`);
            const data = await r.json();
            if (data.results?.[0]?.formatted_address) name = data.results[0].formatted_address;
          } catch (_) {}
        }

        // Last resort: use a readable approximate name
        if (!name) name = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;

        setGeoLoading(false);
        handleLocationSelected({ lat, lng, name, isAutoDetect: true });
        if (onAutoDetect) onAutoDetect({ lat, lng, name });
      },
      (err) => {
        setGeoLoading(false);
        setGeoError(err.code === 1 ? 'Location permission denied' : 'Could not get location');
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };

  return (
    <div className="space-y-2">
      {/* Input row — wrapped in relative for suggestions dropdown */}
      <div className="relative">
        <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-teal-300 bg-white focus-within:ring-2 focus-within:ring-teal-200">
        <MapPin size={14} className="text-teal-500 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={inputText}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          placeholder="Search area in Bengaluru..."
          autoComplete="off"
          className="flex-1 min-w-0 text-sm bg-transparent outline-none text-gray-700 placeholder-gray-400"
          data-testid="where-location-input"
        />
        {value ? (
          <button
            type="button"
            onClick={() => { setInputText(''); setSuggestions([]); onChange(null); if (markerRef.current) markerRef.current.setMap(null); }}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-0.5"
            data-testid="where-clear-location"
            title="Clear location"
          >
            <X size={14} />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleAutoDetect}
            disabled={geoLoading}
            title="Use my current location"
            className="text-teal-500 hover:text-teal-700 flex-shrink-0 p-0.5 disabled:opacity-50 transition-colors"
            data-testid="where-autodetect-btn"
          >
            {geoLoading ? (
              <div className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <LocateFixed size={16} />
            )}
          </button>
        )}
        </div>
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 mt-1 overflow-hidden">
            {suggestions.slice(0, 5).map((item, i) => (
              <button key={i} onMouseDown={() => selectSuggestion(item)}
                className="w-full text-left px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center gap-2">
                <MapPin size={12} className="text-teal-500 flex-shrink-0" />
                <span className="line-clamp-1">{item.text}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {geoError && <p className="text-xs text-red-500 px-1">{geoError}</p>}
      {value?.isAutoDetect && <p className="text-xs text-teal-600 px-1 flex items-center gap-1"><LocateFixed size={11} /> Auto-detected location</p>}

      {/* Distance slider + Show Online — always visible */}
      <div className="px-1 pt-1 pb-0.5">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[11px] text-gray-500">
            Upto <span className="font-semibold text-teal-600">{distanceKm}</span> km
          </p>
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <span className="text-[11px] text-gray-500">Show online</span>
            <span
              onClick={() => onShowOnlineChange && onShowOnlineChange(!showOnline)}
              className={`relative inline-block w-7 h-4 rounded-full transition-colors duration-200 ${showOnline ? 'bg-teal-500' : 'bg-gray-300'}`}
              data-testid="toggle-show-online"
            >
              <span className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform duration-200 ${showOnline ? 'translate-x-3' : 'translate-x-0'}`} />
            </span>
          </label>
        </div>
        <input
          type="range"
          min={5} max={25} step={5}
          value={distanceKm}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (onDistanceChange) onDistanceChange(v);
            if (mapInstanceRef.current) {
              const zoom = v <= 5 ? 13 : v <= 10 ? 12 : v <= 15 ? 11 : v <= 20 ? 11 : 10;
              mapInstanceRef.current.setZoom(zoom);
            }
          }}
          style={{
            WebkitAppearance: 'none',
            appearance: 'none',
            background: `linear-gradient(to right, #14b8a6 0%, #14b8a6 ${((distanceKm - 5) / 20) * 100}%, #e5e7eb ${((distanceKm - 5) / 20) * 100}%, #e5e7eb 100%)`,
            border: 'none',
            outline: 'none',
          }}
          className="w-full h-1.5 rounded-full cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-teal-500 [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-teal-500 [&::-moz-range-thumb]:border-0 [&::-moz-range-track]:border-0 [&::-moz-range-track]:bg-transparent"
          data-testid="distance-slider"
        />
        <div className="flex justify-between text-[10px] text-gray-400 mt-1 select-none">
          <span>5</span><span>10</span><span>15</span><span>20</span><span>25</span>
        </div>
      </div>

      {/* Map — kept in DOM so Maps can init; hidden via opacity until tiles load */}
      {!API_KEY ? (
        <div className="rounded-xl bg-gray-100 h-20 flex items-center justify-center text-xs text-gray-400">Map unavailable</div>
      ) : skipMap ? (
        <div className="rounded-xl bg-gray-50 border border-gray-100 h-20 flex flex-col items-center justify-center text-center px-3">
          <MapPin size={16} className="text-teal-500 mb-1" />
          <p className="text-xs text-gray-500">Map view available on the live site</p>
        </div>
      ) : (
        <div className="relative rounded-xl overflow-hidden" style={{ height: mapLoaded && !mapError ? 176 : 96 }}>
          <div
            ref={mapRef}
            className="absolute inset-0"
            style={{ opacity: mapLoaded && !mapError ? 1 : 0, pointerEvents: mapLoaded && !mapError ? 'auto' : 'none' }}
            data-testid="where-filter-map"
          />
          {(!mapLoaded || mapError) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3 bg-gray-50 border border-gray-100 rounded-xl">
              {mapError ? (
                <>
                  <MapPin size={18} className="text-teal-500 mb-1" />
                  <p className="text-xs text-gray-500">Map unavailable — type a location to filter by area</p>
                </>
              ) : (
                <div className="w-5 h-5 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
